import { useEffect, useMemo, useRef, useState } from 'react';
import { runInBackground } from '@pillar-portal/api-types';
import type {
    InfiniteScrollListFetchPage,
    InfiniteScrollListFetchParams,
    InfiniteScrollListPage,
    InfiniteScrollListPagination,
    InfiniteScrollListCursor,
} from '@/components/ui/infinite-scroll-list.types';
import { tokenizeOmnisearchQueryText } from '@/lib/omnisearch';

export type ScheduledCursorSearchFetchParams<
    TCursor extends InfiniteScrollListCursor = number,
> = InfiniteScrollListFetchParams<TCursor>;
export type ScheduledCursorSearchPage<
    TItem,
    TCursor extends InfiniteScrollListCursor = number,
> = InfiniteScrollListPage<TItem, TCursor>;
export type ScheduledCursorSearchFetchPage<
    TItem,
    TCursor extends InfiniteScrollListCursor = number,
> = InfiniteScrollListFetchPage<TItem, TCursor>;

export interface UseScheduledCursorSearchOptions<
    TItem,
    TCursor extends InfiniteScrollListCursor = number,
> {
    minQueryLength: number;
    delayMs: number;
    pageSize: number;
    fetchPage: ScheduledCursorSearchFetchPage<TItem, TCursor>;
    getItemFilterText: (item: TItem) => string;
    /**
     * When this token changes, the hook resets its internal state and refetches (if configured).
     * This is used to force reloads when the active role assignment changes or when list data
     * is mutated elsewhere in the app.
     */
    resetToken?: string;
    /**
     * If true, the hook automatically fetches an initial page when the query text is empty.
     * This is needed for table/list pages that must not start empty.
     */
    fetchOnEmptyQuery: boolean;
}

export interface UseScheduledCursorSearchResult<
    TItem,
    TCursor extends InfiniteScrollListCursor = number,
> {
    queryText: string;
    setQueryText: (nextQueryText: string) => void;
    items: ReadonlyArray<TItem>;
    visibleItems: ReadonlyArray<TItem>;
    pagination: InfiniteScrollListPagination<TCursor>;
    isLoading: boolean;
    errorMessage: string | null;
    canLoadMore: boolean;
    loadMore: () => void;
}

export function useScheduledCursorSearch<
    TItem,
    TCursor extends InfiniteScrollListCursor = number,
>(
    options: UseScheduledCursorSearchOptions<TItem, TCursor>
): UseScheduledCursorSearchResult<TItem, TCursor> {
    const [queryText, setQueryText] = useState<string>('');
    const [items, setItems] = useState<ReadonlyArray<TItem>>([]);
    const [pagination, setPagination] = useState<InfiniteScrollListPagination<TCursor>>({
        has_more: false,
    });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const latestRequestIdRef = useRef<number>(0);
    const queryTextRef = useRef<string>('');
    const delayTimerIdRef = useRef<number | null>(null);

    // Track the last *input* we fired on (not just the q we sent).
    const lastFiredInputLengthRef = useRef<number>(0);

    // Track the q that produced the current `items` (used for load-more).
    const activeFetchQueryRef = useRef<string | undefined>(undefined);

    const isInFlightRef = useRef<boolean>(false);
    const resetToken: string = options.resetToken ?? 'default';

    const visibleItems: ReadonlyArray<TItem> = useMemo(() => {
        const normalizedQueryText: string = queryText.trim().toLowerCase();
        if (normalizedQueryText.length === 0) {
            return items;
        }
        return items.filter((item: TItem) =>
            options.getItemFilterText(item).toLowerCase().includes(normalizedQueryText)
        );
    }, [items, options.getItemFilterText, queryText]);

    const clearDelayTimer = () => {
        const timerId: number | null = delayTimerIdRef.current;
        if (timerId === null) {
            return;
        }
        window.clearTimeout(timerId);
        delayTimerIdRef.current = null;
    };

    const runSearch: (inputTextAtFireTime: string, cursor?: TCursor) => Promise<void> = async (
        inputTextAtFireTime: string,
        cursor?: TCursor
    ) => {
        const normalizedInputTextAtFireTime: string = inputTextAtFireTime.trim();
        const queryTerms = tokenizeOmnisearchQueryText(normalizedInputTextAtFireTime);
        const queryToFetch: string | undefined =
            normalizedInputTextAtFireTime.length > 0
                ? normalizedInputTextAtFireTime
                : undefined;

        const requestId: number = latestRequestIdRef.current + 1;
        latestRequestIdRef.current = requestId;
        isInFlightRef.current = true;

        setIsLoading(true);
        setErrorMessage(null);
        activeFetchQueryRef.current = queryToFetch;
        lastFiredInputLengthRef.current = normalizedInputTextAtFireTime.length;

        try {
            const page = await options.fetchPage({
                terms: queryTerms.length > 0 ? queryTerms : undefined,
                queryText: queryToFetch,
                limit: options.pageSize,
                cursor,
            });
            if (latestRequestIdRef.current !== requestId) {
                return;
            }

            setPagination(page.pagination);
            setItems((previousItems: ReadonlyArray<TItem>) =>
                cursor === undefined ? page.items : [...previousItems, ...page.items]
            );
        } catch (err) {
            if (latestRequestIdRef.current !== requestId) {
                return;
            }
            setErrorMessage(err instanceof Error ? err.message : 'Search failed');
        } finally {
            if (latestRequestIdRef.current === requestId) {
                setIsLoading(false);
                isInFlightRef.current = false;
            }
        }

        // Post-response scheduling: decide whether to fire again based on how much the user typed
        // since the last fired query.
        const normalizedCurrentText: string = queryTextRef.current.trim();
        if (normalizedCurrentText.length === 0) {
            clearDelayTimer();
            return;
        }

        const additionalCharsSinceLastFire: number = Math.max(
            0,
            normalizedCurrentText.length - lastFiredInputLengthRef.current
        );
        if (additionalCharsSinceLastFire >= Math.max(1, options.minQueryLength)) {
            clearDelayTimer();
            await runSearch(normalizedCurrentText, undefined);
            return;
        } else if (normalizedCurrentText !== normalizedInputTextAtFireTime) {
            if (delayTimerIdRef.current === null) {
                delayTimerIdRef.current = window.setTimeout(() => {
                    const normalizedAtFireTime: string = queryTextRef.current.trim();
                    if (normalizedAtFireTime.length === 0) {
                        clearDelayTimer();
                        return;
                    }
                    if (isInFlightRef.current) {
                        clearDelayTimer();
                        return;
                    }
                    runInBackground(runSearch(normalizedAtFireTime, undefined), (err) => {
                        console.error('Error running search:', err);
                    });
                    clearDelayTimer();
                }, options.delayMs);
            }
        }
    };

    useEffect(() => {
        // Force-reset local state for scope changes (e.g. role switch or cross-page mutations).
        clearDelayTimer();
        latestRequestIdRef.current = 0;
        lastFiredInputLengthRef.current = 0;
        activeFetchQueryRef.current = undefined;
        isInFlightRef.current = false;
        queryTextRef.current = '';

        setQueryText('');
        setItems([]);
        setPagination({ has_more: false });
        setIsLoading(false);
        setErrorMessage(null);

        // If the page must not start empty, trigger the initial fetch even if queryText was
        // already empty (React will otherwise skip running the queryText effect).
        if (options.fetchOnEmptyQuery) {
            runInBackground(runSearch('', undefined), (err) => {
                console.error('Error running initial fetch after reset:', err);
            });
        }
    }, [resetToken]);

    useEffect(() => {
        queryTextRef.current = queryText;

        const normalized: string = queryText.trim();
        if (normalized.length === 0) {
            clearDelayTimer();
            setErrorMessage(null);
            if (options.fetchOnEmptyQuery) {
                if (isInFlightRef.current) {
                    return;
                }
                // If we previously fetched with a non-empty q, switching back to empty should
                // reload the initial accessible page (q omitted).
                const shouldFetchEmpty: boolean =
                    latestRequestIdRef.current === 0 || activeFetchQueryRef.current !== undefined;
                if (shouldFetchEmpty) {
                    runInBackground(runSearch('', undefined), (err) => {
                        console.error('Error running initial fetch:', err);
                    });
                }
                return;
            }
            setItems([]);
            setPagination({ has_more: false });
            return;
        }
        if (isInFlightRef.current) {
            return;
        }

        if (normalized.length >= options.minQueryLength) {
            // If user reaches the min length before the timer expires, fire immediately.
            clearDelayTimer();
            runInBackground(runSearch(normalized, undefined), (err) => {
                console.error('Error running search:', err);
            });
            return;
        }

        // Start a timer on first character; if the user doesn't reach min length, still fire
        // (but backend will treat it as a list page because q is omitted for short inputs).
        if (delayTimerIdRef.current === null) {
            delayTimerIdRef.current = window.setTimeout(() => {
                const normalizedAtFireTime: string = queryTextRef.current.trim();
                if (normalizedAtFireTime.length === 0) {
                    clearDelayTimer();
                    return;
                }
                if (isInFlightRef.current) {
                    clearDelayTimer();
                    return;
                }
                runInBackground(runSearch(normalizedAtFireTime, undefined), (err) => {
                    console.error('Error running search:', err);
                });
                clearDelayTimer();
            }, options.delayMs);
        }
    }, [options.delayMs, options.minQueryLength, queryText]);

    const canLoadMore: boolean = pagination.has_more && pagination.next_cursor !== undefined;
    const loadMore = () => {
        if (!canLoadMore) {
            return;
        }
        const cursor: TCursor | undefined = pagination.next_cursor;
        if (cursor === undefined) {
            throw new Error('pagination.has_more=true but next_cursor is missing');
        }
        const queryForLoadMore: string | undefined = activeFetchQueryRef.current;
        // Recreate an input text representative of the query that produced current items.
        const syntheticInputText: string = queryForLoadMore ?? queryTextRef.current;
        runInBackground(runSearch(syntheticInputText, cursor), (err) => {
            console.error('Error loading more:', err);
        });
    };

    return {
        queryText,
        setQueryText,
        items,
        visibleItems,
        pagination,
        isLoading,
        errorMessage,
        canLoadMore,
        loadMore,
    };
}
